"use client";

import HowDoesItWork from "@/app/(direct)/homepage/sections/HowDoesItWork";
import TREATMENTS from "@/constants/treatments";
import Link from "next/link";

import KeyBenefits from "../components/KeyBenefits";
import PatientTestimonials from "../components/PatientTestimonials";
import Pricing from "../components/Pricing";
import ResearchStudies from "../components/Research";
import RightForMe from "../components/RightForMe";
import SafetySection from "../components/Safety";
import TopSection from "../components/TopSection";
import Arrow from "../components/svg/Arrow";
import Heart from "../components/svg/Heart";
import Neuron from "../components/svg/Neuron";
import { Benefit } from "../types/Benefit";
import ResearchStudiesProps from "../types/Research";
import RightForMeProps from "../types/RightForMe";

const Content = () => {
    const treatment = TREATMENTS.find(
        (treatment) => treatment.name === "Propranolol",
    );

    if (!treatment) {
        return null;
    }
    const benefits: Benefit[] = [
        {
            title: "Reduce dizziness fatigue and brain fog",
            description: [
                {
                    text: "Propranolol helps ",
                    type: "normal",
                },
                {
                    text: "stabilize and reduce your heart rate",
                    type: "normal",
                    // url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2758650/",
                },
                {
                    text: ", alleviating symptoms like dizziness, fatigue, and cognitive impairment caused by orthostatic intolerance.",
                    type: "normal",
                },
            ],
            icon: Neuron, // Replace with appropriate icon
            bgColor: "bg-[#fff9fd]",
        },
        {
            title: "Reduce tachycardia",
            description: [
                {
                    text: "Low-dose propranolol ",
                    type: "normal",
                },
                {
                    text: "significantly reduces heart palpitations and shakiness",
                    type: "normal",
                    // url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2758650/",
                },
                {
                    text: " in patients with POTS, allowing you to feel calmer and more in control.",
                    type: "normal",
                },
            ],
            icon: Arrow, // Replace with appropriate icon
            bgColor: "bg-[#fff2e4]",
        },
        {
            title: "Improve exercise tolerance:",
            description: [
                {
                    text: "Propranolol improves exercise tolerance by ",
                    type: "normal",
                },
                {
                    text: "allowing better heart rate control",
                    type: "normal",
                    // url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3716342/",
                },
                {
                    text: ". Studies show it correlates with improved VO2 max, helping you expand your energy envelope.",
                    type: "normal",
                },
            ],
            icon: Heart, // Replace with appropriate icon
            bgColor: "bg-[#fae9e4]",
        },
    ];

    const rightForMeData: RightForMeProps = {
        treatmentName: "Propranolol",
        accordionData: [
            {
                title: "What Symptoms Does It Help With?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>Propranolol can help manage:</p>
                        <ul className="space-y-1">
                            <li>Tachycardia (fast heart rate)</li>
                            <li>Heart palpitations</li>
                            <li>Dizziness and lightheadedness</li>
                            <li>Exercise intolerance</li>
                            <li>Orthostatic intolerance</li>
                            <li>Fatigue</li>
                            <li>Brain fog</li>
                            <li>Chest discomfort</li>
                        </ul>
                        <p>
                            Patients experiencing these symptoms due to Long
                            COVID, ME/CFS, POTS, or dysautonomia may benefit
                            from propranolol.
                        </p>
                    </div>
                ),
            },
            {
                title: "Is It Safe?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>
                            Propranolol is generally safe and well-tolerated,
                            but side effects can occur, including:
                        </p>
                        <ul className="space-y-1">
                            <li>Headaches</li>
                            <li>Fatigue</li>
                            <li>Restlessness</li>
                            <li>Dizziness or weakness</li>
                            <li>Nausea or vomiting</li>
                        </ul>
                        <p>More severe side effects may include:</p>
                        <ul className="space-y-1">
                            <li>Chest tightness</li>
                            <li>Difficulty breathing</li>
                            <li>Worsening chest pain</li>
                            <li>Very slow heartbeat</li>
                        </ul>
                    </div>
                ),
            },
            {
                title: "Who Should Not Take Propranolol?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>Propranolol is not recommended for patients with:</p>
                        <ul className="space-y-1">
                            <li>
                                Severe respiratory conditions (e.g., asthma)
                            </li>
                            <li>Severe bradycardia (very low heart rate)</li>
                            <li>Heart block or congestive heart failure</li>
                            <li>Depression or thyroid disorders</li>
                        </ul>
                    </div>
                ),
            },
            {
                title: "How Do You Take Propranolol?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>
                            Doses of propranolol through RTHM Direct begin at
                            the very low dose of 2.5mg. If well-tolerated, this
                            amount will increase weekly until the desired
                            therapeutic effect is achieved. You must maintain a
                            detailed log of your heart rate, blood pressure, and
                            symptoms to help your provider find the right dose
                            for you.
                        </p>
                    </div>
                ),
            },
        ],
    };

    const researchData: ResearchStudiesProps = {
        headline: "What Does the Research Say?",
        treatmentName: "Propranolol",
        studies: [
            {
                title: "Study #1: Propranolol Improves Orthostatic Intolerance in POTS Patients",
                content: (
                    <div className="opacity-80">
                        <p>
                            Propranolol significantly reduced heart rate and
                            improved symptoms of orthostatic intolerance,
                            helping patients stabilize while upright.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Propranolol decreases tachycardia and improves
                            symptoms in the Postural Tachycardia Syndrome
                            (POTS): Less is More” PMID: 19687359
                        </p>
                        <Link
                            href="https://pmc.ncbi.nlm.nih.gov/articles/PMC2758650/"
                            target="_blank"
                            className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                        >
                            View
                        </Link>
                    </div>
                ),
            },
            {
                title: "Study #2: Enhanced exercise tolerance and VO2 max in POTS patients using propranolol",
                content: (
                    <div className="opacity-80">
                        <p>
                            A clinical study in 11 patients found propranolol
                            improved exercise tolerance and VO2 max in POTS
                            patients by controlling heart rate during physical
                            activity.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Low-dose propranolol and exercise capacity in
                            postural tachycardia syndrome” PMID: 23616163
                        </p>
                        <Link
                            href="https://pmc.ncbi.nlm.nih.gov/articles/PMC3716342/"
                            target="_blank"
                            className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                        >
                            View
                        </Link>
                    </div>
                ),
            },
            {
                title: "Study #2: POTS Patients Taking Propranolol Showed Improvements in Quality of Life and Depression Symptoms",
                content: (
                    <div className="opacity-80">
                        <p>
                            A clinical study showed orthostatic intolerance (OI)
                            symptoms of POTS patients significantly decreased
                            after 1 month of treatments with propranolol, with
                            continued symptom improvement through month 3. The
                            study also showed improvements in quality of life
                            and depression symptoms.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Efficacy of Propranolol, Bisoprolol, and
                            Pyridostigmine for Postural Tachycardia Syndrome: a
                            Randomized Clinical Trial”
                        </p>
                        <Link
                            href="https://pmc.ncbi.nlm.nih.gov/articles/PMC3716342/"
                            target="_blank"
                            className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                        >
                            View
                        </Link>
                    </div>
                ),
            },
        ],
    };

    const testimonialsData = {
        headline: "What People Are Saying About Propranolol",
        testimonials: [
            {
                quote: "79% of patients reported at least some improvement while taking propranolol for POTS.",
                source: "stuffthatworks.health",
                sourceLink:
                    "https://www.stuffthatworks.health/pots/treatments/propranolol-hydrochloride",
            },
            {
                quote: "I’ve been on propranolol for about two+ weeks now, and the difference is insane to me. I can stand and walk around and barely feel a thing. I cooked three meals in a single weekend when I hadn’t been able to cook in over a year. I’m able to take the dog out without getting lightheaded and short of breath. I just interviewed for three jobs (I haven’t worked in over a year)! I finally got my driver’s permit (a huge feat).",
                author: "u/krobie77",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/POTS/comments/15ehft9/propranolol_changed_my_life/",
            },
            {
                quote: "saw my cardiologist the other day and i was prescribed propranolol for POTS and yesterday was the first day i took it and as i was standing up my heart rate was only 85-95 … usually it’s anywhere between 110-140 while standing. i’m amazed",
                author: "u/tiredmonarch",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/POTS/comments/1dw19o1/propranolol_is_insane/",
            },
            {
                quote: "Propranolol helped me so much. I almost entirely stopped fainting and I can walk around stores etc much more easily, without a cane for dizziness and fatigue that I used to use most of the time. I started out on 10mg twice a day, then 20mg twice a day, now 60mg extended release in the morning.No noticeable side effects for me, which was a pleasant surprise.",
                author: "u/sgsduke",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/POTS/comments/p31t7e/propranolol_does_it_help/",
            },
            {
                quote: "Propranolol helped me more than anything else for <Long COVID>. Its obliterated my POTS symptoms. However, if i miss a dose its incredibly noticeable",
                author: "u/Key_Chart_8624",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/covidlonghaulers/comments/1e5ulpd/propranolol/",
            },
        ],
    };

    return (
        <div className="w-full h-full flex flex-col gap-16">
            <TopSection
                treatmentName={treatment.name}
                treatmentId={treatment.id}
                title="Help Your Heart Find Its Normal Rhythm"
                subtitle="Slow down your heart rate and say goodbye to palpitations with propranolol, an FDA-approved beta-blocker designed to improve symptoms of dysautonomia, POTS, Long COVID, and ME/CFS."
            />
            <HowDoesItWork />
            <Pricing treatment={treatment} />
            <KeyBenefits benefits={benefits} />
            <RightForMe {...rightForMeData} />
            <ResearchStudies {...researchData} />
            <PatientTestimonials {...testimonialsData} />
            <SafetySection treatmentName={treatment.name} />
        </div>
    );
};

export default Content;
